type DataLayerData = Record<
  string,
  string | number | boolean | undefined | null
>

type TrackSwitchPageData = {
  pageURL: string
  pagePath: string
  pageTitle: string
  pageReferrer?: string
  pageLanguage: string
  routeName: string
  nodeId?: string | null
  logoThemeId?: string
  contextChannelName: string
}

type UseAnalytics = {
  trackEvent: (data: DataLayerData | string) => void
  /**
   * Add the switchPage event.
   *
   * @param data - The data to send
   * @param noEvent - Whether to omit the "event" property from the pushed item.
   */
  trackSwitchPage: (data: TrackSwitchPageData, noEvent?: boolean) => void
  getAnalyticsRouteName: () => string
}

export function useAnalytics(): UseAnalytics {
  const app = useNuxtApp()
  const route = useRoute()

  function trackEvent(data: DataLayerData | string) {
    const event = typeof data === 'string' ? { event: data } : data

    if (import.meta.dev) {
      console.table(event)
    }

    // On the client: Push into the dataLayer array.
    if (import.meta.client) {
      try {
        if (!window.dataLayer) {
          window.dataLayer = []
        }
        window.dataLayer.push(event)
      } catch {
        // Noop.
      }
    }

    // On the server: Add a script tag that will push the event into the
    // dataLayer on page load.
    if (import.meta.server) {
      if (!app.ssrContext?.head) {
        return
      }

      const json = (
        import.meta.dev ? JSON.stringify(event, null, 2) : JSON.stringify(event)
      ).replace('"MAGIC_REFERRER_STRING"', 'document.referrer')

      app.ssrContext.head.push({
        script: [
          {
            innerHTML: `window.dataLayer.push(${json})`,
            tagPriority: -999,
          },
        ],
      })
    }
  }

  function trackSwitchPage(data: TrackSwitchPageData, noEvent?: boolean) {
    trackEvent({
      event: noEvent ? undefined : 'switchPage',
      ...data,
    })
  }

  function getAnalyticsRouteName() {
    const name = route.name?.toString()
    if (!name) {
      return ''
    }
    const parts = name.split('___')
    return parts[0] as string
  }

  return { trackEvent, trackSwitchPage, getAnalyticsRouteName }
}

declare global {
  interface Window {
    dataLayer: DataLayerData[]
  }
}
